"use client";

/**
 * The inside of an open nav dropdown. Two layouts, chosen by shape:
 *
 *   • 3+ grouped items → wide multi-column "mega" grid (Admin).
 *   • 1–2 grouped items, or no groups → single tall column with section
 *     eyebrows between groups and hairline dividers for standalone↔group
 *     transitions (Learn, Results, Reports, etc.).
 *
 * Both receive already-visible items and a row renderer from NavDropdown, which
 * owns hover, anchoring and keyboard handling.
 */

/** Groups above this many items are too tall for a single grid column — they
 * would force the whole row to their height and leave sibling columns with
 * large empty gaps. Instead they get their own full-width band with items
 * flowing across columns, which is far shorter overall. */
const LARGE_GROUP_THRESHOLD = 6;

const getVisibleSubs = (item) =>
  (item.subItems || []).filter((s) => s.display !== false);

/**
 * How many children an item declares, visible or not. This is what decides the
 * layout (grid vs column), deliberately separate from `getVisibleSubs`, which
 * decides what is drawn.
 */
export const countSubs = (item) =>
  Array.isArray(item.subItems) ? item.subItems.length : 0;

const MEGA_EYEBROW =
  "mb-2 px-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400";
const COLUMN_EYEBROW =
  "px-2.5 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400";

const Divider = () => <div className="my-4 h-px bg-gray-100" />;

/**
 * A group's heading. A group can be a page too (Playbook): its shortcut keys
 * sit on the heading, since the heading is where its name is.
 */
function GroupEyebrow({ item, className, renderShortcut }) {
  // Focus the complete heading so the shortcut has its destination as context.
  const shortcut = renderShortcut?.(item, {
    renderTrigger: (badge) => (
      <div
        className={`${className} flex items-center justify-between gap-3`}
        tabIndex={0}
      >
        <span>{item.label}</span>
        {badge}
      </div>
    ),
  });
  return shortcut || <div className={className}>{item.label}</div>;
}

function MegaGridPanel({ items, renderItemLink, renderShortcut }) {
  // Split items into sections so standalones don't sit in the same grid as
  // groups (different content lengths would create orphaned vertical gaps in
  // columns).
  const firstGroupIdx = items.findIndex((i) => getVisibleSubs(i).length > 0);
  const leadingStandalones =
    firstGroupIdx >= 0
      ? items.slice(0, firstGroupIdx).filter((i) => getVisibleSubs(i).length === 0)
      : [];
  const groups = items.filter((i) => getVisibleSubs(i).length > 0);
  const smallGroups = groups.filter(
    (i) => getVisibleSubs(i).length < LARGE_GROUP_THRESHOLD,
  );
  const largeGroups = groups.filter(
    (i) => getVisibleSubs(i).length >= LARGE_GROUP_THRESHOLD,
  );
  const trailingStandalones =
    firstGroupIdx >= 0
      ? items
          .slice(firstGroupIdx + 1)
          .filter((i) => getVisibleSubs(i).length === 0)
      : [];

  const itemsGrid = (rows) => (
    <ul className="grid grid-cols-2 gap-x-8 gap-y-0.5 md:grid-cols-3">
      {rows.map((item) => (
        <li key={item.href || item.label}>{renderItemLink(item)}</li>
      ))}
    </ul>
  );

  const standaloneBlock = (rows, eyebrow) => (
    <div>
      <div className={MEGA_EYEBROW}>{eyebrow}</div>
      {itemsGrid(rows)}
    </div>
  );

  return (
    <>
      {leadingStandalones.length > 0 && (
        <>
          {standaloneBlock(leadingStandalones, "Overview")}
          <Divider />
        </>
      )}

      {smallGroups.length > 0 && (
        <div className="grid grid-cols-2 items-start gap-x-8 gap-y-6 md:grid-cols-3">
          {smallGroups.map((item) => (
            <div key={item.href || item.label} className="min-w-0">
              <GroupEyebrow
                item={item}
                className={MEGA_EYEBROW}
                renderShortcut={renderShortcut}
              />
              <ul className="space-y-0.5">
                {getVisibleSubs(item).map((sub) => (
                  <li key={sub.href}>{renderItemLink(sub)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {largeGroups.map((item, gi) => {
        const hasPrecedingContent =
          leadingStandalones.length > 0 || smallGroups.length > 0 || gi > 0;
        return (
          <div key={item.href || item.label}>
            {hasPrecedingContent && <Divider />}
            <GroupEyebrow
              item={item}
              className={MEGA_EYEBROW}
              renderShortcut={renderShortcut}
            />
            {itemsGrid(getVisibleSubs(item))}
          </div>
        );
      })}

      {trailingStandalones.length > 0 && (
        <>
          <Divider />
          {standaloneBlock(trailingStandalones, "More")}
        </>
      )}
    </>
  );
}

/**
 * Single tall column. Every block has an uppercase eyebrow above it — grouped
 * items use the group's own label; sequential standalone items use shared
 * "Overview" (before any group) or "More" (after). Hairline dividers run
 * between blocks when the kind changes.
 */
function ColumnPanel({ items, renderItemLink, renderShortcut }) {
  const hasAnyGroup = items.some((i) => countSubs(i) > 0);
  const firstGroupIdx = items.findIndex((i) => getVisibleSubs(i).length > 0);

  return (
    <div className="px-2">
      {items.map((item, idx) => {
        const subs = getVisibleSubs(item);
        const isGroup = subs.length > 0;
        const prev = items[idx - 1];
        const prevIsGroup = prev && getVisibleSubs(prev).length > 0;

        // First standalone in a sequence gets a section eyebrow ("Overview" if
        // before any group, "More" otherwise). Only show standalone eyebrows
        // when the dropdown also has groups — pure-flat dropdowns stay clean.
        const isFirstStandaloneInSequence =
          !isGroup && (idx === 0 || prevIsGroup);
        const showStandaloneEyebrow = hasAnyGroup && isFirstStandaloneInSequence;
        const standaloneEyebrowLabel =
          firstGroupIdx >= 0 && idx < firstGroupIdx ? "Overview" : "More";

        // Divider between adjacent blocks of different kinds.
        const showDivider = idx > 0 && (isGroup || prevIsGroup);

        return (
          <div key={item.href || item.label}>
            {showDivider && <div className="my-2 h-px bg-gray-100" />}
            {isGroup ? (
              <>
                <GroupEyebrow
                  item={item}
                  className={COLUMN_EYEBROW}
                  renderShortcut={renderShortcut}
                />
                <ul className="space-y-0.5">
                  {subs.map((sub) => (
                    <li key={sub.href}>{renderItemLink(sub)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                {showStandaloneEyebrow && (
                  <div className={COLUMN_EYEBROW}>{standaloneEyebrowLabel}</div>
                )}
                {renderItemLink(item)}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function NavDropdownPanel({
  items,
  useMegaGrid,
  renderItemLink,
  renderShortcut,
}) {
  const Panel = useMegaGrid ? MegaGridPanel : ColumnPanel;
  return (
    <div className={useMegaGrid ? "p-5" : "py-2"}>
      <Panel
        items={items}
        renderItemLink={renderItemLink}
        renderShortcut={renderShortcut}
      />
    </div>
  );
}
